import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useState, useEffect, useRef } from "react";
import Helpers from "@/Helpers";
import type { SectionControlElements } from "@/sections/sectionRuntimeTypes";


const DEFAULT_BUTTON_TEXT = {
  nextPage: "Next Page",
  nextSection: "Next Section",
  completeQuestionnaire: "Complete Questionnaire",
  next: "Next",
};

const DEFAULT_ERRORS = {
  one: "Oops! 1 item needs attention.",
  many: (count: number) => `Oops! ${count} items need attention.`,
};

function getIsLastSection(testState: any, currentSection: any) {
  const sections = testState?.testTypes?.[0]?.sections;
  if (!Array.isArray(sections) || !currentSection?.id) return false;
  const ordered = [...sections].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const last = ordered[ordered.length - 1];
  return last && Number(last.id) === Number(currentSection.id);
}

function resolveButtonLabels(controlElements?: SectionControlElements) {
  return {
    nextPage: Helpers.pickControlText(controlElements?.buttons?.nextPage, DEFAULT_BUTTON_TEXT.nextPage),
    nextSection: Helpers.pickControlText(controlElements?.buttons?.nextSection, DEFAULT_BUTTON_TEXT.nextSection),
    complete: Helpers.pickControlText(controlElements?.buttons?.complete, DEFAULT_BUTTON_TEXT.completeQuestionnaire),
  };
}

export default function SectionControls(props: any) {
  const [disabled, updateDisabled] = useState(false);
  const [clickLocked, setClickLocked] = useState(false);
  const clickLockedRef = useRef(false);
  const prevAdvanceInProgressRef = useRef(false);
  const [timeoutExpired, setTimeoutExpired] = useState(false);

  const testState = props?.testState;
  const currentSection = props?.currentSection;
  const page = Number(props?.page ?? 1);
  const pages = Number(currentSection?.pages ?? 0);
  const nextType = (page && pages && page < pages) ? "page" : "section";
  const controlElements = props?.controlElements as SectionControlElements | undefined;

  useEffect(() => {
    if (props?.isAdmin) {
      setTimeoutExpired(true);
    }
    else {
      setTimeoutExpired(false);
      const timer = setTimeout(() => {
        setTimeoutExpired(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentSection?.id, page, props?.isAdmin, props?.lang]);

  useEffect(() => {
    clickLockedRef.current = false;
    setClickLocked(false);
  }, [currentSection?.id, page]);

  useEffect(() => {
    const wasAdvancing = prevAdvanceInProgressRef.current;
    const isAdvancing = !!props.advanceInProgress;
    prevAdvanceInProgressRef.current = isAdvancing;

    if (wasAdvancing && !isAdvancing && !props.isAdmin) {
      clickLockedRef.current = false;
      setClickLocked(false);
    }
  }, [props.advanceInProgress, props.isAdmin]);

  useEffect(() => {
    var newValue = !timeoutExpired;
    const disableWhenPending = !!props.buttonDisablable || !!props.forceDisableOnPending;
    if (disableWhenPending)
      newValue = newValue || (props.showValidations && props.issueCount > 0);
    if (clickLocked || props.advanceInProgress) {
      newValue = true;
    }
    updateDisabled(newValue);
  }, [page, props.buttonDisablable, props.forceDisableOnPending, props.issueCount, props.showValidations, timeoutExpired, clickLocked, props.advanceInProgress]);

  if (!props || !currentSection) return null;

  const { onNextSection, onNextPage, showValidations, issueCount } = props;

  const labels = resolveButtonLabels(controlElements);
  const onLastPage = !pages || page >= pages;
  const isLastSection = getIsLastSection(testState, currentSection);

  let buttonText = DEFAULT_BUTTON_TEXT.next;
  if (onLastPage) {
    buttonText = isLastSection ? labels.complete : labels.nextSection;
  } else {
    buttonText = labels.nextPage;
  }

  let buttonClass = (issueCount > 0 && showValidations) ? "sectionControls pending" : "sectionControls";
  let oopsMessage: any = "";
  if (issueCount > 0 && showValidations) {
    const missingTemplate = Helpers.pickControlText(controlElements?.errors?.missing);
    if (missingTemplate) {
      const withCount = missingTemplate.replace(/\{count\}/gi, String(issueCount));
      oopsMessage = Helpers.shouldRenderContentAsHtml(withCount) ? parseSanitizedHtml(withCount) : withCount;
    } else {
      oopsMessage = issueCount > 1
        ? DEFAULT_ERRORS.many(issueCount)
        : DEFAULT_ERRORS.one;
    }
  }

  async function onNextAsync() {
    if (clickLockedRef.current || props.advanceInProgress) {
      return;
    }
    if (!props.isAdmin) {
      clickLockedRef.current = true;
      setClickLocked(true);
      updateDisabled(true);
    }
    if (page && pages && page < pages)
      onNextPage();
    else
      onNextSection();
  }

  return currentSection && currentSection.id && (
    <div className={buttonClass}>
      <div className="sectionControls button-container">
        {buttonText && <button className={`okButton ${nextType}`} onClick={onNextAsync} disabled={disabled}>{buttonText}</button>}
        {showValidations && issueCount > 0 &&
          <span className="oops">{oopsMessage||""}</span>
        }
      </div>
    </div>
  );
}
